import { createAxiosInstance, getAuthToken } from "@/dataProvider/axios";
import env from "@/config/env";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { useShallow } from "zustand/react/shallow";
import { getDefaultOrgId } from "@/utils/defaultOrg";

const cleanBaseUrl = (value: string): string => value.replace(/\/+$/, "");

const proxyBaseUrl = cleanBaseUrl(env.API_URL);

export const AGENTS_API_BASE_URL = cleanBaseUrl(
  env.AGENTS_API_URL || `${proxyBaseUrl}/proxy/agents`
);

export const PLAYGROUND_API_BASE_URL = cleanBaseUrl(
  env.PLAYGROUND_API_URL || `${proxyBaseUrl}/proxy/playground`
);

export const skillsAxios = createAxiosInstance(PLAYGROUND_API_BASE_URL);
export const agentsAxios = createAxiosInstance(AGENTS_API_BASE_URL);

export function useSkillsRequestContext(): { orgId: string | null; userId: string | null } {
  const { selectedOrgId, organizations, userId } = useAuthStoreV1(
    useShallow((s) => ({
      selectedOrgId: s.selectedOrg?.id || null,
      organizations: s.organizations,
      userId: s.user?.id || null,
    }))
  );
  const fallbackOrgId =
    selectedOrgId ||
    getDefaultOrgId() ||
    readBrowserOrgId() ||
    (organizations.length === 1 ? organizations[0].id : null);

  return {
    orgId: fallbackOrgId,
    userId,
  };
}

export function skillsApiUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/api/proxy/")) return path;
  if (path.startsWith("/api/agents") || path.startsWith("/api/agent-runs")) {
    return `${AGENTS_API_BASE_URL}${path}`;
  }
  if (path.startsWith("/api/playbooks") || path.startsWith("/api/playbook-runs")) {
    return `${PLAYGROUND_API_BASE_URL}${path}`;
  }
  if (path.startsWith("/api/dropdown") || path.startsWith("/api/tools/")) {
    return `${AGENTS_API_BASE_URL}${path}`;
  }
  return `${PLAYGROUND_API_BASE_URL}${path}`;
}

export function skillsAuthHeaders(orgId?: string | null): Record<string, string> {
  const state = useAuthStoreV1.getState();
  const effectiveOrgId = orgId || resolveCurrentOrgId(state);
  const headers: Record<string, string> = {};
  if (effectiveOrgId) {
    headers["X-Organization-ID"] = effectiveOrgId;
  }
  if (state.user?.id) headers["X-User-ID"] = state.user.id;
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function resolveCurrentOrgId(state: ReturnType<typeof useAuthStoreV1.getState>): string | null {
  if (state.selectedOrg?.id) return state.selectedOrg.id;
  const defaultOrgId = getDefaultOrgId();
  if (defaultOrgId) return defaultOrgId;
  const legacyOrgId = readBrowserOrgId();
  if (legacyOrgId) return legacyOrgId;
  return state.organizations.length === 1 ? state.organizations[0].id : null;
}

function readBrowserOrgId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem("org_id");
    if (stored) return stored;
    const cookie = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith("org_id="));
    return cookie ? decodeURIComponent(cookie.slice("org_id=".length)) : null;
  } catch {
    return null;
  }
}
